import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  PoundSterling, FileText, Receipt, Percent, Users, 
  Plus, Eye, Edit, Trash2, Download, Send, Clock,
  TrendingUp, CheckCircle2, AlertTriangle, Calendar,
  User, Baby, Save, X, CreditCard, BarChart3, FileOutput,
  Printer, Mail, Settings, ToggleLeft, ToggleRight, RefreshCw,
  Bell, AlarmClock, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

// Session Types for percentage splits
const SESSION_TYPES = [
  { value: 'Initial/First Appointment', label: 'Initial/First Appointment', amount: 150 },
  { value: 'Regular Session (1hr)', label: 'Regular Session (1hr)', amount: 70 },
  { value: 'Regular Session (1.5hr)', label: 'Regular Session (1.5hr)', amount: 100 },
  { value: 'Online Consultation (1hr)', label: 'Online Consultation (1hr)', amount: 70 },
  { value: 'Online Consultation (1.5hr)', label: 'Online Consultation (1.5hr)', amount: 100 },
  { value: 'Sensory Profile 2', label: 'Sensory Profile 2', amount: 300 },
  { value: 'Evaluation Report', label: 'Evaluation Report', amount: 70 },
  { value: 'Custom', label: 'Custom Amount', amount: 0 },
];

// Payment Types for invoice
const PAYMENT_TYPES = [
  { value: 'percentage', label: 'Percentage Based' },
  { value: 'hourly', label: 'Hourly Rate' },
];

// Default hourly rates
const DEFAULT_HOURLY_RATES = {
  therapist: 45,
  rehab_assistant: 25,
  clinical_lead: 55,
};

const RECIPIENT_TYPES = [
  { value: 'therapist', label: 'Therapist' },
  { value: 'centre', label: 'Centre' },
  { value: 'admin', label: 'Admin' },
  { value: 'other', label: 'Other' },
];

export default function UnifiedFinancePage() {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Overview data
  const [stats, setStats] = useState({
    totalRevenue: 0,
    pendingInvoices: 0,
    paidInvoices: 0,
    totalSessions: 0
  });
  
  // Invoice data
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);
  const [invoiceToEmail, setInvoiceToEmail] = useState(null);
  const [parentEmail, setParentEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Session records data
  const [sessionRecords, setSessionRecords] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [showGenerateInvoiceDialog, setShowGenerateInvoiceDialog] = useState(false);
  const [selectedSessionForInvoice, setSelectedSessionForInvoice] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  
  // Professional finance data
  const [payslips, setPayslips] = useState([]);
  const [incomeSettings, setIncomeSettings] = useState([]);
  
  // Payment reminders data
  const [pendingReminders, setPendingReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [showRemindersDialog, setShowRemindersDialog] = useState(false);
  const [processingReminders, setProcessingReminders] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Financial statements data
  const [profitLoss, setProfitLoss] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loadingStatements, setLoadingStatements] = useState(false);
  const [statementPeriod, setStatementPeriod] = useState({
    start_date: new Date(new Date().getFullYear(), 3, 6).toISOString().split('T')[0], // April 6 (UK tax year)
    end_date: new Date().toISOString().split('T')[0]
  });
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference: ''
  });
  
  // Expense categories based on standard P&L
  const EXPENSE_CATEGORIES = [
    'Accountancy fees', 'Advertising/Promotional', 'Bank charges',
    'Cleaning & Laundry', 'Director salary', 'Employer NI',
    'Equipment hire charges', 'Insurance', 'IT & Software',
    'Legal and professional fees', 'Pension expenses',
    'Printing, Postage and Stationery', 'Rate', 'Recycling & Waste',
    'Rent or Lease of Buildings', 'Repair and maintenance',
    'Salary and wages', 'Telephone & Internet', 'Travel',
    'Utilities', 'Water', 'Other Expenses'
  ];
  
  // Permissions
  const isDirector = user?.role === 'director';
  const isFinance = ['director', 'finance'].includes(user?.role);
  const isMainAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const canEdit = isFinance || isMainAdmin;
  
  // Report categories state
  const [reportCategories, setReportCategories] = useState({
    expense_categories: [],
    income_categories: [],
    cost_of_sales_categories: []
  });
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    type: 'expense',
    description: ''
  });

  // Session record form
  const [sessionForm, setSessionForm] = useState({
    professional_id: '',
    professional_name: '',
    patient_id: '',
    patient_name: '',
    session_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '10:00',
    session_type: 'Regular Session (1hr)',
    total_amount: 70,
    splits: [
      { recipient_type: 'therapist', percentage: 60, notes: '' },
      { recipient_type: 'centre', percentage: 40, notes: '' }
    ],
    notes: ''
  });

  // Invoice form
  const [invoiceForm, setInvoiceForm] = useState({
    patient_id: '',
    patient_name: '',
    payment_type: 'percentage', // 'percentage' or 'hourly'
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    // Hourly pay fields
    therapist_id: '',
    hourly_rate: 45,
    hours_worked: 1,
    // Percentage fields
    session_type: 'Regular Session (1hr)',
    therapist_percentage: 60,
    centre_percentage: 40,
    notes: '',
    due_date: ''
  });

  // Check if user is reception (limited access)
  const isReception = user?.role === 'reception';
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const isFinanceAccess = ['director', 'managing_director', 'finance'].includes(user?.role) || isSuperAdmin;

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const requests = [
        api.get('/moneysoft-finance/invoices').catch(() => ({ data: [] })),
        api.get('/finance/percentages/session-records').catch(() => ({ data: [] })),
        api.get('/patients').catch(() => ({ data: [] })),
        api.get('/staff').catch(() => ({ data: [] })),
        api.get('/professional-finance/payslips').catch(() => ({ data: [] }))
      ];

      const [invRes, sessRes, patRes, staffRes, payRes] = await Promise.all(requests);
      
      setInvoices(invRes.data || []);
      setSessionRecords(sessRes.data || []);
      setPatients(patRes.data || []);
      setProfessionals(staffRes.data || []);
      setPayslips(payRes.data || []);

      // Calculate stats
      const totalRevenue = (invRes.data || [])
        .filter(i => i.status === 'paid')
        .reduce((sum, i) => sum + (i.total || 0), 0);
      
      const pendingInvoices = (invRes.data || []).filter(i => i.status === 'pending' || i.status === 'sent').length;
      const paidInvoices = (invRes.data || []).filter(i => i.status === 'paid').length;
      
      setStats({
        totalRevenue,
        pendingInvoices,
        paidInvoices,
        totalSessions: (sessRes.data || []).length
      });

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);
  
  // Fetch financial statements
  const fetchFinancialStatements = async () => {
    setLoadingStatements(true);
    try {
      const [plRes, bsRes, invRes, expRes] = await Promise.all([
        api.get(`/finance/statements/profit-loss?start_date=${statementPeriod.start_date}&end_date=${statementPeriod.end_date}`).catch(() => ({ data: null })),
        api.get(`/finance/statements/balance-sheet?as_of_date=${statementPeriod.end_date}`).catch(() => ({ data: null })),
        api.get(`/finance/statements/invoice-summary?start_date=${statementPeriod.start_date}&end_date=${statementPeriod.end_date}`).catch(() => ({ data: null })),
        api.get(`/finance/expenses?start_date=${statementPeriod.start_date}&end_date=${statementPeriod.end_date}`).catch(() => ({ data: [] }))
      ]);
      
      setProfitLoss(plRes.data);
      setBalanceSheet(bsRes.data);
      setInvoiceSummary(invRes.data);
      setExpenses(expRes.data || []);
    } catch (error) {
      console.error('Error fetching financial statements:', error);
      toast.error('Failed to load financial statements');
    } finally {
      setLoadingStatements(false);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'statements' || activeTab === 'expenses') {
      fetchFinancialStatements();
    }
    if (activeTab === 'reminders') {
      fetchPendingReminders();
    }
  }, [activeTab, statementPeriod]);
  
  // Payment Reminders Functions
  const fetchPendingReminders = async () => {
    setLoadingReminders(true);
    try {
      const [remindersRes, schedulerRes] = await Promise.all([
        api.get('/moneysoft-finance/reminders/pending'),
        api.get('/moneysoft-finance/reminders/scheduler-status').catch(() => ({ data: null }))
      ]);
      setPendingReminders(remindersRes.data?.reminders || []);
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      console.error('Error fetching pending reminders:', error);
    } finally {
      setLoadingReminders(false);
    }
  };
  
  const processAllReminders = async () => {
    setProcessingReminders(true);
    try {
      const res = await api.post('/moneysoft-finance/reminders/process');
      if (res.data?.reminders_sent > 0) {
        toast.success(`${res.data.reminders_sent} payment reminder(s) sent successfully!`);
      } else {
        toast.info('No reminders needed at this time');
      }
      await fetchPendingReminders();
    } catch (error) {
      console.error('Error processing reminders:', error);
      toast.error('Failed to process reminders');
    } finally {
      setProcessingReminders(false);
    }
  };
  
  const sendSingleReminder = async (invoiceId, reminderType) => {
    try {
      await api.post(`/moneysoft-finance/reminders/send-single/${invoiceId}?reminder_type=${reminderType}`);
      toast.success('Reminder sent successfully!');
      await fetchPendingReminders();
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };
  
  const getReminderStatusBadge = (status) => {
    const badges = {
      'approaching': { color: 'bg-yellow-100 text-yellow-800', label: 'Due Soon' },
      'due_today': { color: 'bg-orange-100 text-orange-800', label: 'Due Today' },
      'overdue_1_day': { color: 'bg-red-100 text-red-800', label: '1 Day Overdue' },
      'overdue_3_days': { color: 'bg-red-200 text-red-900', label: '3 Days Overdue' },
      'overdue_7_days': { color: 'bg-red-300 text-red-900', label: '7 Days Overdue' },
      'severely_overdue': { color: 'bg-red-500 text-white', label: 'Severely Overdue' }
    };
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={badge.color}>{badge.label}</Badge>;
  };
  
  // Expense handlers
  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      await api.post('/finance/expenses', {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      });
      toast.success('Expense recorded successfully');
      setShowExpenseDialog(false);
      setExpenseForm({
        category: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer',
        reference: ''
      });
      fetchFinancialStatements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record expense');
    }
  };
  
  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;
    try {
      await api.delete(`/finance/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchFinancialStatements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };
  
  // Category management functions
  const fetchReportCategories = async () => {
    try {
      const res = await api.get('/finance/categories');
      setReportCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };
  
  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post('/finance/categories', newCategory);
      toast.success(`${newCategory.type.replace('_', ' ')} category added`);
      setShowCategoryDialog(false);
      setNewCategory({ name: '', type: 'expense', description: '' });
      fetchReportCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };
  
  const handleToggleCategory = async (categoryId, isActive) => {
    try {
      await api.put(`/finance/categories/${categoryId}?is_active=${!isActive}`);
      toast.success(isActive ? 'Category deactivated' : 'Category activated');
      fetchReportCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    }
  };
  
  const handleDeleteCategory = async (categoryId, isDefault) => {
    if (isDefault) {
      toast.error('Default categories cannot be deleted. You can deactivate them instead.');
      return;
    }
    if (!confirm('Are you sure you want to delete this category?')) return;
    try {
      await api.delete(`/finance/categories/${categoryId}`);
      toast.success('Category deleted');
      fetchReportCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };
  
  const initializeDefaultCategories = async () => {
    if (!confirm('This will reset all categories to defaults. Continue?')) return;
    try {
      await api.post('/finance/categories/initialize-defaults');
      toast.success('Categories reset to defaults');
      fetchReportCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize categories');
    }
  };
  
  useEffect(() => {
    if (activeTab === 'settings') {
      fetchReportCategories();
    }
  }, [activeTab]);

  // Session record handlers
  const openNewSession = () => {
    setEditingSession(null);
    setSessionForm({
      professional_id: '',
      professional_name: '',
      patient_id: '',
      patient_name: '',
      session_date: new Date().toISOString().split('T')[0],
      start_time: '09:00',
      end_time: '10:00',
      session_type: 'Regular Session (1hr)',
      total_amount: 70,
      splits: [
        { recipient_type: 'therapist', percentage: 60, notes: '' },
        { recipient_type: 'centre', percentage: 40, notes: '' }
      ],
      notes: ''
    });
    setShowSessionDialog(true);
  };

  const openEditSession = (record) => {
    setEditingSession(record);
    setSessionForm({
      professional_id: record.professional_id,
      professional_name: record.professional_name,
      patient_id: record.patient_id,
      patient_name: record.patient_name,
      session_date: record.session_date,
      start_time: record.start_time || '09:00',
      end_time: record.end_time || '10:00',
      session_type: record.session_type,
      total_amount: record.total_amount,
      splits: record.splits || [],
      notes: record.notes || ''
    });
    setShowSessionDialog(true);
  };

  const handleProfessionalChange = (id) => {
    const prof = professionals.find(p => p.id === id);
    if (prof) {
      setSessionForm(prev => ({
        ...prev,
        professional_id: id,
        professional_name: `${prof.first_name} ${prof.last_name}`
      }));
    }
  };

  const handlePatientChange = (id) => {
    const patient = patients.find(p => p.id === id);
    if (patient) {
      setSessionForm(prev => ({
        ...prev,
        patient_id: id,
        patient_name: `${patient.first_name} ${patient.last_name}`
      }));
    }
  };

  const handleSessionTypeChange = (type) => {
    const sessionType = SESSION_TYPES.find(t => t.value === type);
    setSessionForm(prev => ({
      ...prev,
      session_type: type,
      total_amount: sessionType?.amount || prev.total_amount
    }));
  };

  const updateSplit = (index, field, value) => {
    setSessionForm(prev => ({
      ...prev,
      splits: prev.splits.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const addSplit = () => {
    setSessionForm(prev => ({
      ...prev,
      splits: [...prev.splits, { recipient_type: 'other', percentage: 0, notes: '' }]
    }));
  };

  const removeSplit = (index) => {
    setSessionForm(prev => ({
      ...prev,
      splits: prev.splits.filter((_, i) => i !== index)
    }));
  };

  const calculateSplitTotal = () => {
    return sessionForm.splits.reduce((sum, s) => sum + (parseFloat(s.percentage) || 0), 0);
  };

  const saveSession = async () => {
    if (!sessionForm.professional_id || !sessionForm.patient_id) {
      toast.error('Please select both professional and patient');
      return;
    }
    const total = calculateSplitTotal();
    if (Math.abs(total - 100) > 0.01) {
      toast.error(`Percentages must total 100%. Current: ${total}%`);
      return;
    }

    try {
      if (editingSession) {
        await api.put(`/finance/percentages/session-records/${editingSession.id}`, sessionForm);
        toast.success('Session record updated');
      } else {
        await api.post('/finance/percentages/session-records', sessionForm);
        toast.success('Session record created');
      }
      setShowSessionDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save session');
    }
  };

  const deleteSession = async (id) => {
    if (!confirm('Delete this session record?')) return;
    try {
      await api.delete(`/finance/percentages/session-records/${id}`);
      toast.success('Session deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const deleteInvoice = async (id) => {
    if (!confirm('Are you sure you want to permanently delete this invoice?\n\nThis action cannot be undone.')) return;
    try {
      await api.delete(`/moneysoft-finance/invoices/${id}`);
      toast.success('Invoice deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const deletePayslip = async (id) => {
    if (!confirm('Are you sure you want to permanently delete this payslip?\n\nThis action cannot be undone.')) return;
    try {
      await api.delete(`/professional-finance/payslips/${id}`);
      toast.success('Payslip deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payslip');
    }
  };

  // Print Parent-Facing Invoice (without internal payment details)
  const handlePrintInvoice = (invoice) => {
    // Create a printable invoice window with only parent-facing information
    const printWindow = window.open('', '_blank');
    const invoiceDate = new Date(invoice.created_at).toLocaleDateString('en-GB');
    const dueDate = invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-GB') : 'N/A';
    
    // Get session/service description
    const serviceDescription = invoice.items?.[0]?.description || 'Therapy Session';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; max-width: 800px; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #1e3a5f; }
          .logo { font-size: 28px; font-weight: bold; color: #1e3a5f; }
          .logo span { color: #2a9d8f; }
          .company-info { text-align: right; font-size: 12px; color: #666; line-height: 1.6; }
          .invoice-title { font-size: 32px; color: #1e3a5f; margin-bottom: 30px; }
          .invoice-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 40px; }
          .meta-box { background: #f8f9fa; padding: 20px; border-radius: 8px; }
          .meta-box h3 { font-size: 12px; text-transform: uppercase; color: #666; margin-bottom: 10px; letter-spacing: 1px; }
          .meta-box p { font-size: 14px; line-height: 1.6; }
          .meta-box .highlight { font-size: 18px; font-weight: bold; color: #1e3a5f; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #1e3a5f; color: white; padding: 15px; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; }
          td { padding: 15px; border-bottom: 1px solid #eee; }
          .amount { text-align: right; }
          .total-row { background: #f8f9fa; font-weight: bold; }
          .total-row td { border-bottom: none; font-size: 18px; }
          .total-amount { color: #2a9d8f; font-size: 24px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666; text-align: center; }
          .status { display: inline-block; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: bold; text-transform: uppercase; }
          .status-draft { background: #fef3c7; color: #92400e; }
          .status-sent { background: #dbeafe; color: #1e40af; }
          .status-paid { background: #d1fae5; color: #065f46; }
          .status-overdue { background: #fee2e2; color: #991b1b; }
          .payment-info { background: #f0fdf4; padding: 20px; border-radius: 8px; margin-top: 30px; border-left: 4px solid #2a9d8f; }
          .payment-info h3 { color: #065f46; margin-bottom: 10px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo">ICRCL<span>.</span></div>
            <p style="font-size: 12px; color: #666; margin-top: 5px;">Innovative Care and Rehab Centre Limited</p>
          </div>
          <div class="company-info">
            <p>City Gate House</p>
            <p>246-250 Romford Road</p>
            <p>London, E7 9HZ</p>
            <p>United Kingdom</p>
            <p style="margin-top: 8px;"><strong>Tel:</strong> +44 7380 350059</p>
            <p><strong>Email:</strong> info@icrcl.co.uk</p>
          </div>
        </div>
        
        <h1 class="invoice-title">INVOICE</h1>
        
        <div class="invoice-meta">
          <div class="meta-box">
            <h3>Invoice Details</h3>
            <p><strong>Invoice Number:</strong> ${invoice.invoice_number}</p>
            <p><strong>Date:</strong> ${invoiceDate}</p>
            <p><strong>Due Date:</strong> ${dueDate}</p>
            <p style="margin-top: 10px;"><span class="status status-${invoice.status}">${invoice.status}</span></p>
          </div>
          <div class="meta-box">
            <h3>Bill To</h3>
            <p class="highlight">${invoice.patient_name || 'Patient'}</p>
            ${invoice.family_name ? `<p>Family: ${invoice.family_name}</p>` : ''}
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Date</th>
              <th class="amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items?.map(item => `
              <tr>
                <td>${item.description || 'Therapy Session'}</td>
                <td>${invoiceDate}</td>
                <td class="amount">£${(item.unit_price * (item.quantity || 1)).toFixed(2)}</td>
              </tr>
            `).join('') || `
              <tr>
                <td>${serviceDescription}</td>
                <td>${invoiceDate}</td>
                <td class="amount">£${invoice.total?.toFixed(2)}</td>
              </tr>
            `}
            <tr class="total-row">
              <td colspan="2"><strong>Total Amount Due</strong></td>
              <td class="amount total-amount">£${invoice.total?.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        
        ${invoice.notes ? `<p style="margin-bottom: 20px; font-style: italic; color: #666;">Note: ${invoice.notes}</p>` : ''}
        
        <div class="payment-info">
          <h3>Payment Information</h3>
          <p><strong>Payment Terms:</strong> ${invoice.payment_terms || 'Payment required within 24 hours'}</p>
          <p style="margin-top: 10px;">Please make payment by bank transfer or contact us for other payment options.</p>
        </div>
        
        <div class="footer">
          <p>Thank you for choosing ICRCL for your child's therapy needs.</p>
          <p style="margin-top: 8px;"><strong>Contact Us:</strong> Tel: +44 7380 350059 | Email: info@icrcl.co.uk</p>
          <p style="margin-top: 5px;">City Gate House, 246-250 Romford Road, London, E7 9HZ</p>
        </div>
        
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Send Invoice to Parent via Email
  const handleSendInvoiceEmail = async () => {
    if (!invoiceToEmail || !parentEmail.trim()) {
      toast.error('Please enter parent email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await api.post(`/moneysoft-finance/invoices/${invoiceToEmail.id}/send-email`, null, {
        params: { parent_email: parentEmail }
      });
      
      if (response.data.success) {
        toast.success(`Invoice sent successfully to ${parentEmail}`);
        setShowSendEmailDialog(false);
        setInvoiceToEmail(null);
        setParentEmail('');
        fetchAllData(); // Refresh to update invoice status
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setSendingEmail(false);
    }
  };

  // Open Send Email Dialog
  const openSendEmailDialog = (invoice) => {
    setInvoiceToEmail(invoice);
    // Try to get parent email from patient data
    const patient = patients.find(p => p.id === invoice.patient_id);
    setParentEmail(patient?.parent_email || invoice.parent_email || '');
    setShowSendEmailDialog(true);
  };

  // Generate Invoice from Session Record
  const openGenerateInvoice = (session) => {
    setSelectedSessionForInvoice(session);
    setShowGenerateInvoiceDialog(true);
  };

  const generateInvoiceFromSession = async () => {
    if (!selectedSessionForInvoice) return;
    
    setGeneratingInvoice(true);
    try {
      // Create invoice data from session
      const invoiceData = {
        patient_id: selectedSessionForInvoice.patient_id,
        patient_name: selectedSessionForInvoice.patient_name,
        items: [{
          description: `${selectedSessionForInvoice.session_type} - ${selectedSessionForInvoice.professional_name} (${new Date(selectedSessionForInvoice.session_date).toLocaleDateString('en-GB')} ${selectedSessionForInvoice.start_time || ''}-${selectedSessionForInvoice.end_time || ''})`,
          quantity: 1,
          unit_price: selectedSessionForInvoice.total_amount
        }],
        notes: selectedSessionForInvoice.notes || '',
        session_record_id: selectedSessionForInvoice.id,
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 30 days from now
      };

      // Create the invoice
      const response = await api.post('/moneysoft-finance/invoices', invoiceData);
      
      // Link the session to the invoice
      if (response.data?.id) {
        await api.put(`/finance/percentages/session-records/${selectedSessionForInvoice.id}/link-invoice`, null, {
          params: { invoice_id: response.data.id }
        });
      }
      
      toast.success('Invoice generated successfully!');
      setShowGenerateInvoiceDialog(false);
      setSelectedSessionForInvoice(null);
      fetchAllData();
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'invoiced': return 'bg-blue-100 text-blue-800';
      default: return 'bg-amber-100 text-amber-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="unified-finance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-2">
            <PoundSterling className="w-7 h-7" />
            Finance Management
          </h1>
          <p className="text-slate-500 mt-1">Invoices, session records, and professional earnings</p>
        </div>
        
        {canEdit && (
          <div className="flex gap-2">
            <Button onClick={openNewSession} className="bg-[#1e3a5f]">
              <Plus className="w-4 h-4 mr-2" />
              New Session Record
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold">£{stats.totalRevenue.toLocaleString()}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Pending Invoices</p>
                <p className="text-2xl font-bold">{stats.pendingInvoices}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Paid Invoices</p>
                <p className="text-2xl font-bold">{stats.paidInvoices}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Session Records</p>
                <p className="text-2xl font-bold">{stats.totalSessions}</p>
              </div>
              <Receipt className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="overview" data-testid="overview-tab">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          {/* Sessions tab - Super Admin only (sensitive payment data) */}
          {isSuperAdmin && (
            <TabsTrigger value="sessions" data-testid="sessions-tab">
              <Percent className="w-4 h-4 mr-2" />
              % Sessions
            </TabsTrigger>
          )}
          {/* Invoices tab - visible to all with finance access */}
          <TabsTrigger value="invoices" data-testid="invoices-tab">
            <FileText className="w-4 h-4 mr-2" />
            Invoices
          </TabsTrigger>
          {/* Earnings - Super Admin only (sensitive payment data) */}
          {isSuperAdmin && (
            <TabsTrigger value="earnings" data-testid="earnings-tab">
              <Users className="w-4 h-4 mr-2" />
              Earnings
            </TabsTrigger>
          )}
          {/* Statements - hidden from reception */}
          {!isReception && (
            <TabsTrigger value="statements" data-testid="statements-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Statements
            </TabsTrigger>
          )}
          {/* Expenses - hidden from reception */}
          {!isReception && (
            <TabsTrigger value="expenses" data-testid="expenses-tab">
              <Receipt className="w-4 h-4 mr-2" />
              Expenses
            </TabsTrigger>
          )}
          {/* Payment Reminders - Super Admin and Directors */}
          {isSuperAdmin && (
            <TabsTrigger value="reminders" data-testid="reminders-tab">
              <Bell className="w-4 h-4 mr-2" />
              Reminders
              {pendingReminders.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs">
                  {pendingReminders.length}
                </Badge>
              )}
            </TabsTrigger>
          )}
          {/* Settings - Super Admin only */}
          {isMainAdmin && (
            <TabsTrigger value="settings" data-testid="settings-tab">
              <Settings className="w-4 h-4 mr-2" />
              Report Settings
            </TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Sessions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Recent Session Records
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sessionRecords.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No session records yet</p>
                ) : (
                  <div className="space-y-3">
                    {sessionRecords.slice(0, 5).map((record) => {
                      const therapistSplit = record.splits?.find(s => s.recipient_type === 'therapist');
                      return (
                        <div key={record.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{record.professional_name}</p>
                            <p className="text-xs text-slate-500">{record.patient_name} • {new Date(record.session_date).toLocaleDateString('en-GB')}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">£{record.total_amount?.toFixed(2)}</p>
                            {/* Only show therapist split to Super Admin */}
                            {isSuperAdmin && (
                              <p className="text-xs text-green-600">{therapistSplit?.percentage || 0}% therapist</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Invoices */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Recent Invoices
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No invoices yet</p>
                ) : (
                  <div className="space-y-3">
                    {invoices.slice(0, 5).map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{inv.invoice_number}</p>
                          <p className="text-xs text-slate-500">{inv.patient_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">£{inv.total?.toFixed(2)}</p>
                          <Badge className={getStatusColor(inv.status)}>{inv.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Session Records Tab */}
        <TabsContent value="sessions">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Percent className="w-5 h-5" />
                    Session Percentage Records
                  </CardTitle>
                  <CardDescription>Track therapy sessions with professional, patient, and percentage splits</CardDescription>
                </div>
                {canEdit && (
                  <Button onClick={openNewSession} className="bg-[#1e3a5f]">
                    <Plus className="w-4 h-4 mr-2" />
                    New Session
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {sessionRecords.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No session records yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date & Time</TableHead>
                        <TableHead>Professional</TableHead>
                        <TableHead>Patient</TableHead>
                        <TableHead>Session Type</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Therapist</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessionRecords.map((record) => {
                        const therapistSplit = record.splits?.find(s => s.recipient_type === 'therapist');
                        return (
                          <TableRow key={record.id}>
                            <TableCell>
                              <div className="font-medium">{new Date(record.session_date).toLocaleDateString('en-GB')}</div>
                              <div className="text-xs text-slate-500">{record.start_time || '--:--'} - {record.end_time || '--:--'}</div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                {record.professional_name}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Baby className="w-4 h-4 text-slate-400" />
                                {record.patient_name}
                              </div>
                            </TableCell>
                            <TableCell>{record.session_type}</TableCell>
                            <TableCell className="text-right font-medium">£{record.total_amount?.toFixed(2)}</TableCell>
                            <TableCell className="text-right">
                              <span className="text-green-600 font-medium">{therapistSplit?.percentage || 0}%</span>
                              <span className="text-slate-400 ml-1">(£{therapistSplit?.amount?.toFixed(2) || '0.00'})</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={getStatusColor(record.status)}>{record.status || 'pending'}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex justify-center gap-1">
                                {canEdit && record.status !== 'invoiced' && record.status !== 'paid' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="text-blue-600 hover:text-blue-800"
                                    onClick={() => openGenerateInvoice(record)}
                                    title="Generate Invoice"
                                  >
                                    <FileOutput className="w-4 h-4" />
                                  </Button>
                                )}
                                {canEdit && (
                                  <>
                                    <Button size="sm" variant="ghost" onClick={() => openEditSession(record)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    {/* Delete button - Super Admin only */}
                                    {isMainAdmin && (
                                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteSession(record.id)}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          {/* ICRCL Pricing Guide */}
          <Card className="mb-6 bg-gradient-to-r from-[#1e3a5f]/5 to-[#2a9d8f]/5 border-[#2a9d8f]/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <PoundSterling className="w-4 h-4 text-[#2a9d8f]" />
                ICRCL Standard Pricing Guide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {SESSION_TYPES.filter(t => t.value !== 'Custom').map((type, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">{type.label}</p>
                    <p className="text-lg font-bold text-[#1e3a5f]">£{type.amount}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Invoice Manager
                  </CardTitle>
                  <CardDescription>Manage patient invoices and payments</CardDescription>
                </div>
                <Button onClick={() => setShowInvoiceDialog(true)} className="bg-[#2a9d8f] hover:bg-[#238b7e]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No invoices yet</p>
                  <Button 
                    variant="link" 
                    className="text-[#2a9d8f] mt-2"
                    onClick={() => setShowInvoiceDialog(true)}
                  >
                    Create your first invoice
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Patient</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                          <TableCell>{inv.patient_name}</TableCell>
                          <TableCell>{new Date(inv.created_at).toLocaleDateString('en-GB')}</TableCell>
                          <TableCell className="text-right font-medium">£{inv.total?.toFixed(2)}</TableCell>
                          <TableCell className="text-center">
                            <Badge className={getStatusColor(inv.status)}>{inv.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => setSelectedInvoice(inv)}
                                title="View Invoice Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-blue-600 hover:text-blue-800"
                                onClick={() => openSendEmailDialog(inv)}
                                title="Send Invoice to Parent via Email"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handlePrintInvoice(inv)}
                                title="Print Invoice for Parent"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                              {/* Delete button - Super Admin only */}
                              {isMainAdmin && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-500"
                                  onClick={() => deleteInvoice(inv.id)}
                                  title="Delete Invoice (Super Admin Only)"
                                  data-testid="delete-invoice-btn"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Professional Earnings Tab */}
        <TabsContent value="earnings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Professional Earnings
              </CardTitle>
              <CardDescription>View earnings and payslips for professionals</CardDescription>
            </CardHeader>
            <CardContent>
              {payslips.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No payslip records yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Professional</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payslips.map((slip) => (
                        <TableRow key={slip.id}>
                          <TableCell className="font-medium">{slip.professional_name}</TableCell>
                          <TableCell>{slip.period}</TableCell>
                          <TableCell className="text-right">£{slip.gross_pay?.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium">£{slip.net_pay?.toFixed(2)}</TableCell>
                          <TableCell className="text-center">
                            <Badge className={getStatusColor(slip.status)}>{slip.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              <Button size="sm" variant="ghost">
                                <Eye className="w-4 h-4" />
                              </Button>
                              {/* Delete button - Super Admin only */}
                              {isMainAdmin && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-500"
                                  onClick={() => deletePayslip(slip.id)}
                                  title="Delete Payslip (Super Admin Only)"
                                  data-testid="delete-payslip-btn"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Statements Tab */}
        <TabsContent value="statements">
          <div className="space-y-6">
            {/* Period Selector */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Label>Period:</Label>
                    <Input 
                      type="date" 
                      value={statementPeriod.start_date}
                      onChange={(e) => setStatementPeriod(prev => ({...prev, start_date: e.target.value}))}
                      className="w-40"
                    />
                    <span>to</span>
                    <Input 
                      type="date" 
                      value={statementPeriod.end_date}
                      onChange={(e) => setStatementPeriod(prev => ({...prev, end_date: e.target.value}))}
                      className="w-40"
                    />
                  </div>
                  <Button onClick={fetchFinancialStatements} disabled={loadingStatements}>
                    {loadingStatements ? 'Loading...' : 'Generate Reports'}
                  </Button>
                  <Button variant="outline" onClick={() => window.print()} className="ml-auto">
                    <Printer className="w-4 h-4 mr-2" />
                    Print Report
                  </Button>
                  <Button 
                    variant="default" 
                    onClick={async () => {
                      try {
                        toast.loading('Generating PDF...');
                        const response = await api.get(`/finance/statements/profit-loss/pdf?start_date=${statementPeriod.start_date}&end_date=${statementPeriod.end_date}`, {
                          responseType: 'blob'
                        });
                        toast.dismiss();
                        // Create download link
                        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `ProfitAndLoss_${statementPeriod.start_date}_to_${statementPeriod.end_date}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('PDF downloaded successfully');
                      } catch (error) {
                        toast.dismiss();
                        toast.error('Failed to download PDF');
                        console.error('PDF download error:', error);
                      }
                    }}
                    disabled={!profitLoss}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Profit & Loss Statement - Matching Reference PDF Format */}
            <Card className="print:shadow-none print:border-0" id="profit-loss-report">
              <CardContent className="p-8">
                {loadingStatements ? (
                  <div className="text-center py-8">Loading...</div>
                ) : profitLoss ? (
                  <div className="max-w-3xl mx-auto font-mono text-sm">
                    {/* Header */}
                    <div className="text-center mb-8 border-b-2 border-black pb-4">
                      <h1 className="text-2xl font-bold text-slate-800">ICRCL Limited</h1>
                      <h2 className="text-xl font-semibold mt-2">Profit and Loss</h2>
                      <p className="text-slate-600 mt-2">
                        {new Date(profitLoss.period.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} to {new Date(profitLoss.period.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{profitLoss.accounting_basis}</p>
                    </div>
                    
                    {/* Column Headers */}
                    <div className="flex justify-end mb-2 pr-4 text-xs font-semibold text-slate-600">
                      <span className="w-28 text-right">TOTAL</span>
                    </div>
                    
                    {/* INCOME Section */}
                    <div className="mb-6">
                      <div className="bg-slate-100 px-3 py-1 font-bold text-slate-800 border-l-4 border-green-600">
                        Income
                      </div>
                      <div className="pl-6 mt-2 space-y-1">
                        {profitLoss.income.categories.length > 0 ? (
                          profitLoss.income.categories.map((cat, idx) => (
                            <div key={idx} className="flex justify-between py-0.5">
                              <span className="text-slate-700">{cat.category}</span>
                              <span className="w-28 text-right">£{cat.amount.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                            </div>
                          ))
                        ) : (
                          <div className="flex justify-between py-0.5 text-slate-400">
                            <span>No income recorded</span>
                            <span className="w-28 text-right">£0.00</span>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between mt-2 pt-1 border-t border-slate-300 font-semibold">
                        <span className="pl-3">Total Income</span>
                        <span className="w-28 text-right text-green-700">£{profitLoss.income.total.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                      </div>
                    </div>
                    
                    {/* COST OF SALES Section */}
                    <div className="mb-6">
                      <div className="bg-slate-100 px-3 py-1 font-bold text-slate-800 border-l-4 border-orange-500">
                        Cost of Sales
                      </div>
                      <div className="pl-6 mt-2 space-y-1">
                        {profitLoss.cost_of_sales.categories.length > 0 ? (
                          profitLoss.cost_of_sales.categories.map((cat, idx) => (
                            <div key={idx} className="flex justify-between py-0.5">
                              <span className="text-slate-700">{cat.category}</span>
                              <span className="w-28 text-right">£{cat.amount.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                            </div>
                          ))
                        ) : (
                          <>
                            <div className="flex justify-between py-0.5 text-slate-400">
                              <span>Cost of Goods Sold</span>
                              <span className="w-28 text-right">£0.00</span>
                            </div>
                            <div className="flex justify-between py-0.5 text-slate-400">
                              <span>Freelancer</span>
                              <span className="w-28 text-right">£0.00</span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="flex justify-between mt-2 pt-1 border-t border-slate-300 font-semibold">
                        <span className="pl-3">Total Cost of Sales</span>
                        <span className="w-28 text-right text-orange-600">£{profitLoss.cost_of_sales.total.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                      </div>
                    </div>
                    
                    {/* GROSS PROFIT */}
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded px-4 py-2">
                      <div className="flex justify-between font-bold text-lg">
                        <span>Gross Profit</span>
                        <span className={`w-28 text-right ${profitLoss.gross_profit >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                          £{profitLoss.gross_profit.toLocaleString('en-GB', {minimumFractionDigits: 2})}
                        </span>
                      </div>
                    </div>
                    
                    {/* EXPENSES Section */}
                    <div className="mb-6">
                      <div className="bg-slate-100 px-3 py-1 font-bold text-slate-800 border-l-4 border-red-500">
                        Expenses
                      </div>
                      <div className="pl-6 mt-2 space-y-1">
                        {/* Show all expense categories from the PDF template */}
                        {[
                          'Accountancy fees', 'Advertising/Promotional', 'Bank charges',
                          'Cleaning & Laundry', 'Director salary', 'Employer NI',
                          'Equipment hire charges', 'Insurance', 'IT & Software',
                          'Legal and professional fees', 'Pension expenses',
                          'Printing, Postage and Stationery', 'Rate', 'Recycling & Waste',
                          'Rent or Lease of Buildings', 'Repair and maintenance',
                          'Salary and wages', 'Telephone & Internet', 'Travel',
                          'Utilities', 'Water', 'Other Expenses'
                        ].map((expCategory) => {
                          const expData = profitLoss.expenses.categories.find(e => e.category === expCategory);
                          const amount = expData?.amount || 0;
                          return (
                            <div key={expCategory} className={`flex justify-between py-0.5 ${amount > 0 ? '' : 'text-slate-400'}`}>
                              <span>{expCategory}</span>
                              <span className="w-28 text-right">£{amount.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-2 pt-1 border-t border-slate-300 font-semibold">
                        <span className="pl-3">Total Expenses</span>
                        <span className="w-28 text-right text-red-600">£{profitLoss.expenses.total.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                      </div>
                    </div>
                    
                    {/* NET OPERATING INCOME */}
                    <div className="mb-4 border-t-2 border-slate-400 pt-3">
                      <div className="flex justify-between font-semibold">
                        <span>Net Operating Income</span>
                        <span className={`w-28 text-right ${profitLoss.net_operating_income >= 0 ? 'text-slate-800' : 'text-red-700'}`}>
                          {profitLoss.net_operating_income < 0 ? '-' : ''}£{Math.abs(profitLoss.net_operating_income).toLocaleString('en-GB', {minimumFractionDigits: 2})}
                        </span>
                      </div>
                    </div>
                    
                    {/* Net Other Income */}
                    <div className="mb-4">
                      <div className="flex justify-between text-slate-600">
                        <span>Net Other Income</span>
                        <span className="w-28 text-right">£{(profitLoss.net_other_income || 0).toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                      </div>
                    </div>
                    
                    {/* NET INCOME - Final Line */}
                    <div className="bg-slate-800 text-white px-4 py-3 rounded">
                      <div className="flex justify-between font-bold text-lg">
                        <span>Net Income</span>
                        <span className={`w-28 text-right ${profitLoss.net_income >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {profitLoss.net_income < 0 ? '-' : ''}£{Math.abs(profitLoss.net_income).toLocaleString('en-GB', {minimumFractionDigits: 2})}
                        </span>
                      </div>
                    </div>
                    
                    {/* Footer */}
                    <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500 text-center">
                      <p>Generated on {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} at {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                      <p className="mt-1">ICRCL Healthcare Platform - Financial Statements</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <TrendingUp className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg">Select a period and click "Generate Reports"</p>
                    <p className="text-sm mt-2">to view your Profit and Loss statement</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Invoice Summary */}
            {invoiceSummary && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Invoice Summary & Aging Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">£{invoiceSummary.summary.total_value.toLocaleString('en-GB', {minimumFractionDigits: 2})}</p>
                      <p className="text-sm text-blue-600">Total Invoiced</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">£{invoiceSummary.summary.paid.value.toLocaleString('en-GB', {minimumFractionDigits: 2})}</p>
                      <p className="text-sm text-green-600">Paid ({invoiceSummary.summary.paid.count})</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-700">£{(invoiceSummary.summary.pending.value + invoiceSummary.summary.sent.value).toLocaleString('en-GB', {minimumFractionDigits: 2})}</p>
                      <p className="text-sm text-amber-600">Outstanding ({invoiceSummary.summary.pending.count + invoiceSummary.summary.sent.count})</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-700">£{invoiceSummary.summary.overdue.value.toLocaleString('en-GB', {minimumFractionDigits: 2})}</p>
                      <p className="text-sm text-red-600">Overdue ({invoiceSummary.summary.overdue.count})</p>
                    </div>
                  </div>
                  
                  {/* Collection Rate */}
                  <div className="mb-6">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">Collection Rate</span>
                      <span className="font-bold">{invoiceSummary.summary.collection_rate}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full ${invoiceSummary.summary.collection_rate >= 80 ? 'bg-green-500' : invoiceSummary.summary.collection_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{width: `${Math.min(invoiceSummary.summary.collection_rate, 100)}%`}}
                      />
                    </div>
                  </div>
                  
                  {/* Aging Analysis */}
                  <h4 className="font-semibold mb-3">Aging Analysis (Outstanding Invoices)</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Age</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>Current (Not Yet Due)</TableCell>
                        <TableCell className="text-right">{invoiceSummary.aging_analysis.current.count}</TableCell>
                        <TableCell className="text-right">£{invoiceSummary.aging_analysis.current.value.toLocaleString('en-GB', {minimumFractionDigits: 2})}</TableCell>
                      </TableRow>
                      <TableRow className="bg-amber-50">
                        <TableCell>1-30 Days Overdue</TableCell>
                        <TableCell className="text-right">{invoiceSummary.aging_analysis['1_30_days'].count}</TableCell>
                        <TableCell className="text-right">£{invoiceSummary.aging_analysis['1_30_days'].value.toLocaleString('en-GB', {minimumFractionDigits: 2})}</TableCell>
                      </TableRow>
                      <TableRow className="bg-orange-50">
                        <TableCell>31-60 Days Overdue</TableCell>
                        <TableCell className="text-right">{invoiceSummary.aging_analysis['31_60_days'].count}</TableCell>
                        <TableCell className="text-right">£{invoiceSummary.aging_analysis['31_60_days'].value.toLocaleString('en-GB', {minimumFractionDigits: 2})}</TableCell>
                      </TableRow>
                      <TableRow className="bg-red-50">
                        <TableCell>61-90 Days Overdue</TableCell>
                        <TableCell className="text-right">{invoiceSummary.aging_analysis['61_90_days'].count}</TableCell>
                        <TableCell className="text-right">£{invoiceSummary.aging_analysis['61_90_days'].value.toLocaleString('en-GB', {minimumFractionDigits: 2})}</TableCell>
                      </TableRow>
                      <TableRow className="bg-red-100">
                        <TableCell className="font-semibold">Over 90 Days Overdue</TableCell>
                        <TableCell className="text-right font-semibold">{invoiceSummary.aging_analysis.over_90_days.count}</TableCell>
                        <TableCell className="text-right font-semibold">£{invoiceSummary.aging_analysis.over_90_days.value.toLocaleString('en-GB', {minimumFractionDigits: 2})}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
            
            {/* Balance Sheet */}
            {balanceSheet && (
              <Card className="print:shadow-none print:border-0" id="balance-sheet-report">
                <CardContent className="p-8">
                  {/* Download Button */}
                  <div className="flex justify-end mb-4 print:hidden">
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={async () => {
                        try {
                          toast.loading('Generating Balance Sheet PDF...');
                          const response = await api.get(`/finance/statements/balance-sheet/pdf?as_of_date=${statementPeriod.end_date}`, {
                            responseType: 'blob'
                          });
                          toast.dismiss();
                          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `BalanceSheet_${statementPeriod.end_date}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success('Balance Sheet PDF downloaded');
                        } catch (error) {
                          toast.dismiss();
                          toast.error('Failed to download PDF');
                          console.error('PDF download error:', error);
                        }
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Balance Sheet PDF
                    </Button>
                  </div>
                  <div className="max-w-3xl mx-auto font-mono text-sm">
                    {/* Header - Matching P&L Style */}
                    <div className="text-center mb-8 border-b-2 border-black pb-4">
                      <h1 className="text-2xl font-bold text-slate-800">ICRCL Limited</h1>
                      <h2 className="text-xl font-semibold mt-2">Balance Sheet</h2>
                      <p className="text-slate-600 mt-2">
                        As of {new Date(balanceSheet.as_of_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-8">
                      {/* ASSETS Column */}
                      <div>
                        {/* Current Assets */}
                        <div className="mb-6">
                          <div className="bg-blue-100 px-3 py-1 font-bold text-blue-800 border-l-4 border-blue-600 mb-2">
                            Current Assets
                          </div>
                          <div className="pl-4 space-y-1">
                            {balanceSheet.assets.current_assets.categories.map((cat, idx) => (
                              <div key={idx} className="flex justify-between py-0.5">
                                <span className="text-slate-700">{cat.category}</span>
                                <span className="w-28 text-right">£{cat.amount.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                              </div>
                            ))}
                            <div className="flex justify-between pt-2 border-t font-semibold">
                              <span>Total Current Assets</span>
                              <span className="w-28 text-right">£{balanceSheet.assets.current_assets.total.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Fixed Assets */}
                        <div className="mb-6">
                          <div className="bg-blue-50 px-3 py-1 font-bold text-blue-700 border-l-4 border-blue-400 mb-2">
                            Fixed Assets
                          </div>
                          <div className="pl-4 space-y-1">
                            {balanceSheet.assets.fixed_assets.categories.length > 0 ? (
                              balanceSheet.assets.fixed_assets.categories.map((cat, idx) => (
                                <div key={idx} className="flex justify-between py-0.5">
                                  <span className="text-slate-700">{cat.category}</span>
                                  <span className="w-28 text-right">£{cat.amount.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                                </div>
                              ))
                            ) : (
                              <div className="flex justify-between py-0.5 text-slate-400">
                                <span>No fixed assets recorded</span>
                                <span className="w-28 text-right">£0.00</span>
                              </div>
                            )}
                            <div className="flex justify-between pt-2 border-t font-semibold">
                              <span>Total Fixed Assets</span>
                              <span className="w-28 text-right">£{balanceSheet.assets.fixed_assets.total.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Total Assets */}
                        <div className="bg-blue-800 text-white px-4 py-3 rounded">
                          <div className="flex justify-between font-bold text-lg">
                            <span>TOTAL ASSETS</span>
                            <span>£{balanceSheet.assets.total.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* LIABILITIES & EQUITY Column */}
                      <div>
                        {/* Current Liabilities */}
                        <div className="mb-6">
                          <div className="bg-red-100 px-3 py-1 font-bold text-red-800 border-l-4 border-red-600 mb-2">
                            Current Liabilities
                          </div>
                          <div className="pl-4 space-y-1">
                            {balanceSheet.liabilities.current_liabilities.categories.map((cat, idx) => (
                              <div key={idx} className="flex justify-between py-0.5">
                                <span className="text-slate-700">{cat.category}</span>
                                <span className="w-28 text-right">£{cat.amount.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                              </div>
                            ))}
                            <div className="flex justify-between pt-2 border-t font-semibold">
                              <span>Total Current Liabilities</span>
                              <span className="w-28 text-right">£{balanceSheet.liabilities.current_liabilities.total.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Long-term Liabilities */}
                        {balanceSheet.liabilities.long_term_liabilities && balanceSheet.liabilities.long_term_liabilities.total > 0 && (
                          <div className="mb-6">
                            <div className="bg-red-50 px-3 py-1 font-bold text-red-700 border-l-4 border-red-400 mb-2">
                              Long-term Liabilities
                            </div>
                            <div className="pl-4 space-y-1">
                              {balanceSheet.liabilities.long_term_liabilities.categories.map((cat, idx) => (
                                <div key={idx} className="flex justify-between py-0.5">
                                  <span className="text-slate-700">{cat.category}</span>
                                  <span className="w-28 text-right">£{cat.amount.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Equity */}
                        <div className="mb-6">
                          <div className="bg-green-100 px-3 py-1 font-bold text-green-800 border-l-4 border-green-600 mb-2">
                            Equity
                          </div>
                          <div className="pl-4 space-y-1">
                            {balanceSheet.equity.categories.map((cat, idx) => (
                              <div key={idx} className="flex justify-between py-0.5">
                                <span className="text-slate-700">{cat.category}</span>
                                <span className={`w-28 text-right ${cat.amount < 0 ? 'text-red-600' : ''}`}>
                                  {cat.amount < 0 ? '-' : ''}£{Math.abs(cat.amount).toLocaleString('en-GB', {minimumFractionDigits: 2})}
                                </span>
                              </div>
                            ))}
                            <div className="flex justify-between pt-2 border-t font-semibold">
                              <span>Total Equity</span>
                              <span className={`w-28 text-right ${balanceSheet.equity.total < 0 ? 'text-red-600' : ''}`}>
                                {balanceSheet.equity.total < 0 ? '-' : ''}£{Math.abs(balanceSheet.equity.total).toLocaleString('en-GB', {minimumFractionDigits: 2})}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Total Liabilities & Equity */}
                        <div className="bg-purple-800 text-white px-4 py-3 rounded">
                          <div className="flex justify-between font-bold text-lg">
                            <span>TOTAL L & E</span>
                            <span>£{(balanceSheet.liabilities.total + balanceSheet.equity.total).toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Balance Check */}
                    <div className="mt-6 text-center">
                      {balanceSheet.balance_check ? (
                        <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full">
                          <CheckCircle2 className="w-5 h-5" />
                          <span className="font-medium">Balance Verified: Assets = Liabilities + Equity</span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full">
                          <AlertTriangle className="w-5 h-5" />
                          <span className="font-medium">Balance discrepancy detected</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Footer */}
                    <div className="mt-8 pt-4 border-t text-center text-xs text-slate-500">
                      Generated on {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} at {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      <br />ICRCL Healthcare Platform - Financial Statements
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-red-50 border-red-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-red-600">Total Expenses (Period)</p>
                  <p className="text-2xl font-bold text-red-700">
                    £{expenses.reduce((sum, e) => sum + (e.amount || 0), 0).toLocaleString('en-GB', {minimumFractionDigits: 2})}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-blue-600">This Month</p>
                  <p className="text-2xl font-bold text-blue-700">
                    £{expenses.filter(e => e.date?.startsWith(new Date().toISOString().slice(0, 7))).reduce((sum, e) => sum + (e.amount || 0), 0).toLocaleString('en-GB', {minimumFractionDigits: 2})}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-purple-600">Categories Used</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {new Set(expenses.map(e => e.category)).size}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-green-600">Total Records</p>
                  <p className="text-2xl font-bold text-green-700">{expenses.length}</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Main Expenses Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-red-600" />
                    Business Expenses
                  </CardTitle>
                  <CardDescription>
                    Track all business expenses for accurate P&L reporting
                  </CardDescription>
                </div>
                {canEdit && (
                  <Button onClick={() => setShowExpenseDialog(true)} className="bg-red-600 hover:bg-red-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Record Expense
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {/* Period Filter */}
                <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                  <Label>Filter Period:</Label>
                  <Input 
                    type="date" 
                    value={statementPeriod.start_date}
                    onChange={(e) => setStatementPeriod(prev => ({...prev, start_date: e.target.value}))}
                    className="w-40"
                  />
                  <span>to</span>
                  <Input 
                    type="date" 
                    value={statementPeriod.end_date}
                    onChange={(e) => setStatementPeriod(prev => ({...prev, end_date: e.target.value}))}
                    className="w-40"
                  />
                </div>
                
                {/* Expenses by Category Summary */}
                {expenses.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium mb-3">Expenses by Category</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {Object.entries(
                        expenses.reduce((acc, e) => {
                          acc[e.category] = (acc[e.category] || 0) + e.amount;
                          return acc;
                        }, {})
                      ).sort((a, b) => b[1] - a[1]).map(([category, amount]) => (
                        <div key={category} className="flex justify-between p-2 bg-slate-100 rounded text-sm">
                          <span className="truncate">{category}</span>
                          <span className="font-medium ml-2">£{amount.toLocaleString('en-GB', {minimumFractionDigits: 2})}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Expenses Table */}
                {expenses.length === 0 ? (
                  <div className="text-center py-8">
                    <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-2">No expenses recorded for this period</p>
                    <p className="text-sm text-slate-400">Click "Record Expense" to add business expenses</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Payment Method</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        {isMainAdmin && <TableHead>Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.map((expense) => (
                        <TableRow key={expense.id}>
                          <TableCell>{new Date(expense.date).toLocaleDateString('en-GB')}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{expense.category}</Badge>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{expense.description || '-'}</TableCell>
                          <TableCell className="capitalize">{expense.payment_method?.replace('_', ' ') || '-'}</TableCell>
                          <TableCell>{expense.reference || '-'}</TableCell>
                          <TableCell className="text-right font-medium">
                            £{expense.amount.toLocaleString('en-GB', {minimumFractionDigits: 2})}
                          </TableCell>
                          {isMainAdmin && (
                            <TableCell>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-500"
                                onClick={() => handleDeleteExpense(expense.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
            
            {/* Quick Add Common Expenses */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Reference: Common Expense Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  {EXPENSE_CATEGORIES.map(cat => (
                    <div key={cat} className="p-2 bg-slate-50 rounded border border-slate-200 hover:bg-slate-100 cursor-pointer"
                         onClick={() => { setExpenseForm(prev => ({...prev, category: cat})); setShowExpenseDialog(true); }}>
                      {cat}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Payment Reminders Tab - Super Admin Only */}
        {isSuperAdmin && (
          <TabsContent value="reminders">
            <div className="space-y-6">
              {/* Header with Actions */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="w-5 h-5 text-amber-600" />
                      Payment Reminders
                    </CardTitle>
                    <CardDescription>
                      Manage automatic payment reminder emails to parents for unpaid invoices
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={fetchPendingReminders}
                      disabled={loadingReminders}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingReminders ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button
                      onClick={processAllReminders}
                      disabled={processingReminders || pendingReminders.length === 0}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {processingReminders ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send All Due Reminders
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
              </Card>
              
              {/* Reminder Schedule Info */}
              <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-amber-100 rounded-full">
                      <AlarmClock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-amber-900">Automatic Reminder Schedule</h3>
                        {schedulerStatus && (
                          <div className="flex items-center gap-2">
                            <Badge className={schedulerStatus.running ? 'bg-green-500' : 'bg-red-500'}>
                              {schedulerStatus.running ? 'Scheduler Active' : 'Scheduler Stopped'}
                            </Badge>
                            {schedulerStatus.jobs?.[0]?.next_run && (
                              <span className="text-xs text-amber-700">
                                Next run: {new Date(schedulerStatus.jobs[0].next_run).toLocaleString('en-GB', {
                                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                                })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <div className="bg-white p-2 rounded border border-amber-200">
                          <p className="font-medium text-amber-800">12 Hours Before</p>
                          <p className="text-amber-600 text-xs">Approaching Deadline</p>
                        </div>
                        <div className="bg-white p-2 rounded border border-orange-200">
                          <p className="font-medium text-orange-800">Due Day</p>
                          <p className="text-orange-600 text-xs">Payment Due Today</p>
                        </div>
                        <div className="bg-white p-2 rounded border border-red-200">
                          <p className="font-medium text-red-800">1 Day Overdue</p>
                          <p className="text-red-600 text-xs">First Reminder</p>
                        </div>
                        <div className="bg-white p-2 rounded border border-red-300">
                          <p className="font-medium text-red-800">3 Days Overdue</p>
                          <p className="text-red-600 text-xs">Second Reminder</p>
                        </div>
                        <div className="bg-white p-2 rounded border border-red-400">
                          <p className="font-medium text-red-900">7 Days Overdue</p>
                          <p className="text-red-700 text-xs">Final Reminder</p>
                        </div>
                      </div>
                      {/* Recent scheduler runs */}
                      {schedulerStatus?.recent_runs?.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-amber-200">
                          <p className="text-xs font-medium text-amber-800 mb-2">Recent Automatic Runs:</p>
                          <div className="flex flex-wrap gap-2">
                            {schedulerStatus.recent_runs.slice(0, 3).map((run, idx) => (
                              <div key={idx} className="bg-white px-2 py-1 rounded text-xs border border-amber-200">
                                <span className="text-amber-700">
                                  {new Date(run.executed_at).toLocaleString('en-GB', {
                                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                                  })}
                                </span>
                                <span className="ml-2 font-medium text-amber-900">
                                  {run.reminders_sent} sent
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Pending Reminders List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Invoices Requiring Reminders ({pendingReminders.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingReminders ? (
                    <div className="flex justify-center py-8">
                      <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                  ) : pendingReminders.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">All caught up!</p>
                      <p className="text-slate-500 text-sm">No invoices currently need reminders</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice</TableHead>
                            <TableHead>Patient</TableHead>
                            <TableHead>Parent Email</TableHead>
                            <TableHead className="text-right">Amount Due</TableHead>
                            <TableHead>Due Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Reminders Sent</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingReminders.map((reminder) => (
                            <TableRow key={reminder.invoice_id}>
                              <TableCell className="font-medium">{reminder.invoice_number}</TableCell>
                              <TableCell>{reminder.patient_name}</TableCell>
                              <TableCell className="text-sm text-slate-600">{reminder.parent_email}</TableCell>
                              <TableCell className="text-right font-semibold text-red-600">
                                £{reminder.amount_due?.toFixed(2)}
                              </TableCell>
                              <TableCell>
                                {new Date(reminder.due_date).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </TableCell>
                              <TableCell>{getReminderStatusBadge(reminder.status)}</TableCell>
                              <TableCell>
                                {reminder.reminders_sent?.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {reminder.reminders_sent.map((type, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs">
                                        {type}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-sm">None</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  {reminder.status === 'approaching' && !reminder.reminders_sent?.includes('approaching') && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => sendSingleReminder(reminder.invoice_id, 'approaching')}
                                      className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                                    >
                                      <Mail className="w-3 h-3 mr-1" />
                                      Approaching
                                    </Button>
                                  )}
                                  {reminder.status === 'due_today' && !reminder.reminders_sent?.includes('due_today') && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => sendSingleReminder(reminder.invoice_id, 'due_today')}
                                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                    >
                                      <Mail className="w-3 h-3 mr-1" />
                                      Due Today
                                    </Button>
                                  )}
                                  {['overdue_1_day', 'overdue_3_days', 'overdue_7_days', 'severely_overdue'].includes(reminder.status) && (
                                    <>
                                      {!reminder.reminders_sent?.includes('overdue_1') && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => sendSingleReminder(reminder.invoice_id, 'overdue_1')}
                                          className="text-red-600 border-red-300 hover:bg-red-50"
                                        >
                                          <Mail className="w-3 h-3 mr-1" />
                                          1st
                                        </Button>
                                      )}
                                      {reminder.days_overdue >= 3 && !reminder.reminders_sent?.includes('overdue_3') && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => sendSingleReminder(reminder.invoice_id, 'overdue_3')}
                                          className="text-red-700 border-red-400 hover:bg-red-50"
                                        >
                                          <Mail className="w-3 h-3 mr-1" />
                                          2nd
                                        </Button>
                                      )}
                                      {reminder.days_overdue >= 7 && !reminder.reminders_sent?.includes('overdue_7') && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => sendSingleReminder(reminder.invoice_id, 'overdue_7')}
                                          className="text-red-800 border-red-500 hover:bg-red-50"
                                        >
                                          <Mail className="w-3 h-3 mr-1" />
                                          Final
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Email Status Info */}
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                    <div className="text-sm text-green-800">
                      <p className="font-medium mb-1">Email Delivery Active</p>
                      <p>Payment reminder emails are sent via SMTP from <strong>info@icrcl.co.uk</strong>. 
                      Parents will receive professional HTML emails with invoice details and payment instructions.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
        
        {/* Report Settings Tab - Super Admin Only */}
        {isMainAdmin && (
          <TabsContent value="settings">
            <div className="space-y-6">
              {/* Header Actions */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5 text-slate-600" />
                      P&L Report Categories
                    </CardTitle>
                    <CardDescription>
                      Customize income and expense categories for your Profit & Loss reports
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={initializeDefaultCategories}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reset to Defaults
                    </Button>
                    <Button onClick={() => setShowCategoryDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Category
                    </Button>
                  </div>
                </CardHeader>
              </Card>
              
              {/* Income Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-green-700">Income Categories</CardTitle>
                  <CardDescription>Categories for recording income/revenue</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {reportCategories.income_categories?.length > 0 ? (
                      reportCategories.income_categories.map((cat) => (
                        <div key={cat.id || cat.name} className={`flex items-center justify-between p-3 rounded-lg border ${cat.is_active ? 'bg-white' : 'bg-slate-100 opacity-60'}`}>
                          <div className="flex items-center gap-3">
                            <span className={`font-medium ${!cat.is_active ? 'line-through text-slate-400' : ''}`}>{cat.name}</span>
                            {cat.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                            {!cat.is_active && <Badge className="bg-slate-200 text-slate-600 text-xs">Inactive</Badge>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleToggleCategory(cat.id, cat.is_active)}
                              title={cat.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {cat.is_active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                            </Button>
                            {!cat.is_default && (
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteCategory(cat.id, cat.is_default)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-4">No income categories. Click "Reset to Defaults" to initialize.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Cost of Sales Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-orange-700">Cost of Sales Categories</CardTitle>
                  <CardDescription>Direct costs associated with service delivery</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {reportCategories.cost_of_sales_categories?.length > 0 ? (
                      reportCategories.cost_of_sales_categories.map((cat) => (
                        <div key={cat.id || cat.name} className={`flex items-center justify-between p-3 rounded-lg border ${cat.is_active ? 'bg-white' : 'bg-slate-100 opacity-60'}`}>
                          <div className="flex items-center gap-3">
                            <span className={`font-medium ${!cat.is_active ? 'line-through text-slate-400' : ''}`}>{cat.name}</span>
                            {cat.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                            {!cat.is_active && <Badge className="bg-slate-200 text-slate-600 text-xs">Inactive</Badge>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleToggleCategory(cat.id, cat.is_active)}
                            >
                              {cat.is_active ? <ToggleRight className="w-5 h-5 text-orange-600" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                            </Button>
                            {!cat.is_default && (
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteCategory(cat.id, cat.is_default)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-4">No cost of sales categories. Click "Reset to Defaults" to initialize.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Expense Categories */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-red-700">Expense Categories</CardTitle>
                  <CardDescription>Operating expenses and overheads</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {reportCategories.expense_categories?.length > 0 ? (
                      reportCategories.expense_categories.map((cat) => (
                        <div key={cat.id || cat.name} className={`flex items-center justify-between p-3 rounded-lg border ${cat.is_active ? 'bg-white' : 'bg-slate-100 opacity-60'}`}>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm ${!cat.is_active ? 'line-through text-slate-400' : ''}`}>{cat.name}</span>
                            {cat.is_default && <Badge variant="outline" className="text-xs">D</Badge>}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => handleToggleCategory(cat.id, cat.is_active)}
                            >
                              {cat.is_active ? <ToggleRight className="w-4 h-4 text-red-600" /> : <ToggleLeft className="w-4 h-4 text-slate-400" />}
                            </Button>
                            {!cat.is_default && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteCategory(cat.id, cat.is_default)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-4 col-span-2">No expense categories. Click "Reset to Defaults" to initialize.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Info */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <h4 className="font-medium text-blue-800 mb-2">How Categories Work</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Income Categories</strong>: Used to categorize revenue sources in P&L</li>
                    <li>• <strong>Cost of Sales</strong>: Direct costs (therapist payments, freelancers)</li>
                    <li>• <strong>Expense Categories</strong>: Operating expenses shown in P&L report</li>
                    <li>• <strong>Toggle</strong>: Deactivate categories you don't need (won't show in reports)</li>
                    <li>• <strong>Default categories</strong> cannot be deleted, only deactivated</li>
                    <li>• Changes take effect immediately on the next report generation</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>
      
      {/* Add Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
            <DialogDescription>Add a custom category for your P&L reports</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCategory} className="space-y-4">
            <div className="space-y-2">
              <Label>Category Type *</Label>
              <Select value={newCategory.type} onValueChange={(v) => setNewCategory(prev => ({...prev, type: v}))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="cost_of_sales">Cost of Sales</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input 
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({...prev, name: e.target.value}))}
                placeholder="e.g., Marketing Services"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input 
                value={newCategory.description}
                onChange={(e) => setNewCategory(prev => ({...prev, description: e.target.value}))}
                placeholder="Brief description"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
              <Button type="submit">Add Category</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-red-600" />
              Record Business Expense
            </DialogTitle>
            <DialogDescription>
              Add a new expense record for financial tracking
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateExpense} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input 
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm(prev => ({...prev, date: e.target.value}))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Amount (£) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm(prev => ({...prev, amount: e.target.value}))}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm(prev => ({...prev, category: v}))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select expense category" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input 
                placeholder="Brief description of the expense"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm(prev => ({...prev, description: e.target.value}))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={expenseForm.payment_method} onValueChange={(v) => setExpenseForm(prev => ({...prev, payment_method: v}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="card">Card Payment</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="direct_debit">Direct Debit</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference/Invoice No.</Label>
                <Input 
                  placeholder="INV-001"
                  value={expenseForm.reference}
                  onChange={(e) => setExpenseForm(prev => ({...prev, reference: e.target.value}))}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowExpenseDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700">
                <Save className="w-4 h-4 mr-2" />
                Save Expense
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Session Record Dialog */}
      <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              {editingSession ? 'Edit Session Record' : 'New Session Record'}
            </DialogTitle>
            <DialogDescription>
              Record a therapy session with professional, patient, and percentage splits
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Professional and Patient */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  Professional *
                </Label>
                <Select value={sessionForm.professional_id} onValueChange={handleProfessionalChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select professional..." />
                  </SelectTrigger>
                  <SelectContent>
                    {professionals.filter(p => ['therapist', 'professional', 'psychology', 'special_education'].includes(p.role)).map(prof => (
                      <SelectItem key={prof.id} value={prof.id}>
                        {prof.first_name} {prof.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Baby className="w-4 h-4" />
                  Patient *
                </Label>
                <Select value={sessionForm.patient_id} onValueChange={handlePatientChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select patient..." />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(patient => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.first_name} {patient.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4" />
                  Date *
                </Label>
                <Input
                  type="date"
                  value={sessionForm.session_date}
                  onChange={(e) => setSessionForm(prev => ({ ...prev, session_date: e.target.value }))}
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4" />
                  Start Time *
                </Label>
                <Input
                  type="time"
                  value={sessionForm.start_time}
                  onChange={(e) => setSessionForm(prev => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4" />
                  End Time *
                </Label>
                <Input
                  type="time"
                  value={sessionForm.end_time}
                  onChange={(e) => setSessionForm(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>

            {/* Session Type and Amount */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2">Session Type</Label>
                <Select value={sessionForm.session_type} onValueChange={handleSessionTypeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SESSION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label} {type.amount > 0 && `(£${type.amount})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <PoundSterling className="w-4 h-4" />
                  Total Amount *
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={sessionForm.total_amount}
                  onChange={(e) => setSessionForm(prev => ({ ...prev, total_amount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            {/* Percentage Splits */}
            <div>
              <Label className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Percentage Splits
                </span>
                <Badge className={Math.abs(calculateSplitTotal() - 100) < 0.01 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  Total: {calculateSplitTotal()}%
                </Badge>
              </Label>
              
              <Card className="p-4">
                <div className="space-y-3">
                  {sessionForm.splits.map((split, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <Select value={split.recipient_type} onValueChange={(v) => updateSplit(idx, 'recipient_type', v)}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RECIPIENT_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={split.percentage}
                          onChange={(e) => updateSplit(idx, 'percentage', parseFloat(e.target.value) || 0)}
                          className="w-20 text-center"
                        />
                        <span>%</span>
                      </div>
                      
                      <span className="text-sm text-slate-500 w-20">
                        = £{((sessionForm.total_amount * split.percentage) / 100).toFixed(2)}
                      </span>
                      
                      <Input
                        value={split.notes || ''}
                        onChange={(e) => updateSplit(idx, 'notes', e.target.value)}
                        placeholder="Notes"
                        className="flex-1"
                      />
                      
                      <Button variant="ghost" size="sm" onClick={() => removeSplit(idx)} disabled={sessionForm.splits.length <= 1}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  <Button variant="outline" size="sm" onClick={addSplit}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Split
                  </Button>
                </div>
              </Card>
            </div>

            {/* Notes */}
            <div>
              <Label className="mb-2">Notes</Label>
              <Input
                value={sessionForm.notes}
                onChange={(e) => setSessionForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSessionDialog(false)}>Cancel</Button>
            <Button 
              onClick={saveSession} 
              className="bg-[#1e3a5f]"
              disabled={Math.abs(calculateSplitTotal() - 100) >= 0.01}
            >
              <Save className="w-4 h-4 mr-2" />
              {editingSession ? 'Update' : 'Create'} Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Invoice from Session Dialog */}
      <Dialog open={showGenerateInvoiceDialog} onOpenChange={setShowGenerateInvoiceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileOutput className="w-5 h-5" />
              Generate Invoice from Session
            </DialogTitle>
            <DialogDescription>
              Create an invoice for this therapy session
            </DialogDescription>
          </DialogHeader>

          {selectedSessionForInvoice && (
            <div className="space-y-4">
              {/* Session Summary */}
              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Patient</p>
                      <p className="font-medium">{selectedSessionForInvoice.patient_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Professional</p>
                      <p className="font-medium">{selectedSessionForInvoice.professional_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Session Date</p>
                      <p className="font-medium">
                        {new Date(selectedSessionForInvoice.session_date).toLocaleDateString('en-GB')}
                        {selectedSessionForInvoice.start_time && ` ${selectedSessionForInvoice.start_time}-${selectedSessionForInvoice.end_time}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Session Type</p>
                      <p className="font-medium">{selectedSessionForInvoice.session_type}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Invoice Preview */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Invoice Preview</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        {selectedSessionForInvoice.session_type} - {selectedSessionForInvoice.professional_name}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        £{selectedSessionForInvoice.total_amount?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow className="bg-slate-50">
                      <TableCell className="font-semibold">Total</TableCell>
                      <TableCell className="text-right font-bold text-lg">
                        £{selectedSessionForInvoice.total_amount?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <p className="text-sm text-slate-500">
                This will create an invoice for {selectedSessionForInvoice.patient_name} and mark this session as &quot;invoiced&quot;.
                The invoice will be due in 30 days.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateInvoiceDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generateInvoiceFromSession} 
              className="bg-[#1e3a5f]"
              disabled={generatingInvoice}
            >
              {generatingInvoice ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <FileOutput className="w-4 h-4 mr-2" />
                  Generate Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Create New Invoice
            </DialogTitle>
            <DialogDescription>
              {isSuperAdmin 
                ? 'Create an invoice with percentage or hourly rate payment'
                : 'Create a simple invoice for therapy session'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Patient Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2">Patient/Child *</Label>
                <Select
                  value={invoiceForm.patient_id}
                  onValueChange={(id) => {
                    const patient = patients.find(p => p.id === id);
                    if (patient) {
                      setInvoiceForm(prev => ({
                        ...prev,
                        patient_id: id,
                        patient_name: `${patient.first_name} ${patient.last_name}`
                      }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Therapist Selection - Only for Super Admin */}
              {isSuperAdmin && (
                <div>
                  <Label className="mb-2">Therapist</Label>
                  <Select
                    value={invoiceForm.therapist_id}
                    onValueChange={(id) => {
                      const prof = professionals.find(p => p.id === id);
                      if (prof) {
                        setInvoiceForm(prev => ({
                          ...prev,
                          therapist_id: id,
                          therapist_name: `${prof.first_name} ${prof.last_name}`
                        }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select therapist" />
                    </SelectTrigger>
                    <SelectContent>
                      {professionals.filter(p => ['therapist', 'clinical_lead', 'director'].includes(p.role)).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.first_name} {p.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Simple Invoice Form for Reception/Finance */}
            {!isSuperAdmin && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <PoundSterling className="w-4 h-4" />
                    Session Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="mb-2">Session Type</Label>
                    <Select
                      value={invoiceForm.session_type}
                      onValueChange={(type) => {
                        const sessionType = SESSION_TYPES.find(t => t.value === type);
                        setInvoiceForm(prev => ({
                          ...prev,
                          session_type: type,
                          items: [{
                            description: type,
                            quantity: 1,
                            unit_price: sessionType?.amount || 0
                          }]
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select session type" />
                      </SelectTrigger>
                      <SelectContent>
                        {SESSION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label} - £{type.amount}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="mb-2">Session Fee (£) *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={invoiceForm.items[0]?.unit_price || ''}
                      onChange={(e) => setInvoiceForm(prev => ({
                        ...prev,
                        items: [{
                          ...prev.items[0],
                          unit_price: parseFloat(e.target.value) || 0
                        }]
                      }))}
                      placeholder="e.g., 70.00"
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Total Invoice Amount:</span> £{invoiceForm.items[0]?.unit_price?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Type Selection - Super Admin Only */}
            {isSuperAdmin && (
              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <Label className="mb-3 block font-semibold">Payment Type</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        invoiceForm.payment_type === 'percentage'
                          ? 'border-[#2a9d8f] bg-[#2a9d8f]/10'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setInvoiceForm(prev => ({ ...prev, payment_type: 'percentage' }))}
                      data-testid="payment-type-percentage"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Percent className="w-5 h-5 text-[#2a9d8f]" />
                        <span className="font-medium">Percentage Based</span>
                      </div>
                      <p className="text-sm text-slate-500">Therapist receives a percentage of the session fee</p>
                    </div>

                    <div
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        invoiceForm.payment_type === 'hourly'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setInvoiceForm(prev => ({ ...prev, payment_type: 'hourly' }))}
                      data-testid="payment-type-hourly"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-blue-500" />
                        <span className="font-medium">Hourly Rate</span>
                      </div>
                      <p className="text-sm text-slate-500">Therapist receives a flat hourly rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Percentage Payment Fields - Super Admin Only */}
            {isSuperAdmin && invoiceForm.payment_type === 'percentage' && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Percent className="w-4 h-4" />
                    Percentage Split
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="mb-2">Session Type</Label>
                    <Select
                      value={invoiceForm.session_type}
                      onValueChange={(type) => {
                        const sessionType = SESSION_TYPES.find(t => t.value === type);
                        setInvoiceForm(prev => ({
                          ...prev,
                          session_type: type,
                          items: [{
                            description: type,
                            quantity: 1,
                            unit_price: sessionType?.amount || 0
                          }]
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select session type" />
                      </SelectTrigger>
                      <SelectContent>
                        {SESSION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label} - £{type.amount}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-2">Therapist %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={invoiceForm.therapist_percentage}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          setInvoiceForm(prev => ({
                            ...prev,
                            therapist_percentage: val,
                            centre_percentage: 100 - val
                          }));
                        }}
                      />
                    </div>
                    <div>
                      <Label className="mb-2">Centre %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={invoiceForm.centre_percentage}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          setInvoiceForm(prev => ({
                            ...prev,
                            centre_percentage: val,
                            therapist_percentage: 100 - val
                          }));
                        }}
                      />
                    </div>
                  </div>

                  {/* Earnings Preview */}
                  {invoiceForm.items[0]?.unit_price > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800">
                        <span className="font-medium">Session Fee:</span> £{invoiceForm.items[0]?.unit_price?.toFixed(2)}
                      </p>
                      <p className="text-sm text-green-800">
                        <span className="font-medium">Therapist Earnings:</span> £{((invoiceForm.items[0]?.unit_price || 0) * (invoiceForm.therapist_percentage / 100)).toFixed(2)} ({invoiceForm.therapist_percentage}%)
                      </p>
                      <p className="text-sm text-green-800">
                        <span className="font-medium">Centre Revenue:</span> £{((invoiceForm.items[0]?.unit_price || 0) * (invoiceForm.centre_percentage / 100)).toFixed(2)} ({invoiceForm.centre_percentage}%)
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Hourly Rate Fields - Super Admin Only */}
            {isSuperAdmin && invoiceForm.payment_type === 'hourly' && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Hourly Rate Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-2">Hourly Rate (£)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={invoiceForm.hourly_rate}
                        onChange={(e) => setInvoiceForm(prev => ({
                          ...prev,
                          hourly_rate: parseFloat(e.target.value) || 0
                        }))}
                        placeholder="e.g., 45.00"
                      />
                    </div>
                    <div>
                      <Label className="mb-2">Hours Worked</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={invoiceForm.hours_worked}
                        onChange={(e) => setInvoiceForm(prev => ({
                          ...prev,
                          hours_worked: parseFloat(e.target.value) || 0
                        }))}
                        placeholder="e.g., 1.5"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2">Service Description</Label>
                    <Input
                      value={invoiceForm.items[0]?.description || ''}
                      onChange={(e) => setInvoiceForm(prev => ({
                        ...prev,
                        items: [{ ...prev.items[0], description: e.target.value }]
                      }))}
                      placeholder="e.g., Therapy Session"
                    />
                  </div>

                  <div>
                    <Label className="mb-2">Session Fee (£) - Total to bill patient</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={invoiceForm.items[0]?.unit_price || ''}
                      onChange={(e) => setInvoiceForm(prev => ({
                        ...prev,
                        items: [{
                          ...prev.items[0],
                          unit_price: parseFloat(e.target.value) || 0,
                          quantity: 1
                        }]
                      }))}
                      placeholder="e.g., 70.00"
                    />
                  </div>

                  {/* Earnings Preview */}
                  {invoiceForm.hourly_rate > 0 && invoiceForm.hours_worked > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">Therapist Earnings:</span> £{(invoiceForm.hourly_rate * invoiceForm.hours_worked).toFixed(2)}
                        <span className="text-xs ml-2">(£{invoiceForm.hourly_rate}/hr × {invoiceForm.hours_worked} hrs)</span>
                      </p>
                      {invoiceForm.items[0]?.unit_price > 0 && (
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Centre Revenue:</span> £{(invoiceForm.items[0]?.unit_price - (invoiceForm.hourly_rate * invoiceForm.hours_worked)).toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Due Date & Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2">Due Date</Label>
                <Input
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
              <div>
                <Label className="mb-2">Notes</Label>
                <Input
                  value={invoiceForm.notes}
                  onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!invoiceForm.patient_id) {
                  toast.error('Please select a patient');
                  return;
                }
                if (!invoiceForm.items[0]?.unit_price) {
                  toast.error('Please enter a session fee');
                  return;
                }
                if (!invoiceForm.due_date) {
                  // Default to 24 hours from now (next day)
                  invoiceForm.due_date = new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                }

                try {
                  const payload = {
                    patient_id: invoiceForm.patient_id,
                    items: [{
                      description: invoiceForm.items[0]?.description || invoiceForm.session_type || 'Therapy Session',
                      quantity: 1,
                      unit_price: invoiceForm.items[0]?.unit_price
                    }],
                    due_date: invoiceForm.due_date,
                    notes: invoiceForm.notes,
                    payment_type: invoiceForm.payment_type,
                    therapist_id: invoiceForm.therapist_id,
                    therapist_name: invoiceForm.therapist_name,
                    therapist_percentage: invoiceForm.payment_type === 'percentage' ? invoiceForm.therapist_percentage : null,
                    centre_percentage: invoiceForm.payment_type === 'percentage' ? invoiceForm.centre_percentage : null,
                    hourly_rate: invoiceForm.payment_type === 'hourly' ? invoiceForm.hourly_rate : null,
                    hours_worked: invoiceForm.payment_type === 'hourly' ? invoiceForm.hours_worked : null
                  };

                  await api.post('/moneysoft-finance/invoices', payload);
                  toast.success('Invoice created successfully!');
                  setShowInvoiceDialog(false);
                  // Reset form
                  setInvoiceForm({
                    patient_id: '',
                    patient_name: '',
                    payment_type: 'percentage',
                    items: [{ description: '', quantity: 1, unit_price: 0 }],
                    therapist_id: '',
                    hourly_rate: 45,
                    hours_worked: 1,
                    session_type: 'Regular Session (1hr)',
                    therapist_percentage: 60,
                    centre_percentage: 40,
                    notes: '',
                    due_date: ''
                  });
                  fetchAllData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to create invoice');
                }
              }}
              className="bg-[#2a9d8f]"
              data-testid="create-invoice-submit"
            >
              <FileText className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice View Dialog - Shows internal details for admin */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoice Details - {selectedInvoice?.invoice_number}
            </DialogTitle>
            <DialogDescription>
              Internal view with payment split details (not shown to parents)
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Patient</p>
                  <p className="font-medium">{selectedInvoice.patient_name}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Date</p>
                  <p className="font-medium">{new Date(selectedInvoice.created_at).toLocaleDateString('en-GB')}</p>
                </div>
              </div>
              
              {/* Service Details */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Service Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedInvoice.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2 border-b last:border-0">
                      <span>{item.description}</span>
                      <span className="font-medium">£{(item.unit_price * (item.quantity || 1)).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-2 mt-2 bg-slate-50 px-2 rounded font-bold">
                    <span>Total</span>
                    <span className="text-[#2a9d8f]">£{selectedInvoice.total?.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
              
              {/* Internal Payment Split - Super Admin Only */}
              {isSuperAdmin && (
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      Internal Payment Split (Not shown to parents)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Payment Type</p>
                        <Badge variant="outline" className={selectedInvoice.payment_type === 'hourly' ? 'border-blue-500 text-blue-600' : 'border-purple-500 text-purple-600'}>
                          {selectedInvoice.payment_type === 'hourly' ? 'Hourly Rate' : 'Percentage Split'}
                        </Badge>
                      </div>
                      {selectedInvoice.therapist_name && (
                        <div>
                          <p className="text-xs text-slate-500">Therapist</p>
                          <p className="font-medium">{selectedInvoice.therapist_name}</p>
                        </div>
                      )}
                    </div>
                    
                    {selectedInvoice.payment_type === 'percentage' && (
                      <div className="mt-4 grid grid-cols-2 gap-4">
                        <div className="bg-white p-3 rounded border">
                          <p className="text-xs text-slate-500">Therapist ({selectedInvoice.therapist_percentage}%)</p>
                          <p className="text-lg font-bold text-[#2a9d8f]">£{selectedInvoice.therapist_earnings?.toFixed(2) || '0.00'}</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <p className="text-xs text-slate-500">Centre ({selectedInvoice.centre_percentage}%)</p>
                          <p className="text-lg font-bold text-[#1e3a5f]">£{(selectedInvoice.total - (selectedInvoice.therapist_earnings || 0)).toFixed(2)}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedInvoice.payment_type === 'hourly' && (
                      <div className="mt-4 grid grid-cols-3 gap-4">
                        <div className="bg-white p-3 rounded border">
                          <p className="text-xs text-slate-500">Hourly Rate</p>
                          <p className="font-bold">£{selectedInvoice.hourly_rate?.toFixed(2)}/hr</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <p className="text-xs text-slate-500">Hours Worked</p>
                          <p className="font-bold">{selectedInvoice.hours_worked} hrs</p>
                        </div>
                        <div className="bg-white p-3 rounded border">
                          <p className="text-xs text-slate-500">Therapist Earnings</p>
                          <p className="text-lg font-bold text-[#2a9d8f]">£{selectedInvoice.therapist_earnings?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedInvoice(null)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                openSendEmailDialog(selectedInvoice);
                setSelectedInvoice(null);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send to Parent
            </Button>
            <Button 
              onClick={() => {
                handlePrintInvoice(selectedInvoice);
                setSelectedInvoice(null);
              }}
              className="bg-[#2a9d8f]"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print for Parent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Invoice Email Dialog */}
      <Dialog open={showSendEmailDialog} onOpenChange={setShowSendEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Send Invoice to Parent
            </DialogTitle>
            <DialogDescription>
              Send invoice {invoiceToEmail?.invoice_number} to parent via email. Payment is required within 24 hours.
            </DialogDescription>
          </DialogHeader>
          
          {invoiceToEmail && (
            <div className="space-y-4 py-4">
              {/* Invoice Summary */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-slate-500">Invoice #</p>
                    <p className="font-medium">{invoiceToEmail.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Amount</p>
                    <p className="font-bold text-[#2a9d8f]">£{invoiceToEmail.total?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Patient</p>
                    <p className="font-medium">{invoiceToEmail.patient_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Status</p>
                    <Badge className={getStatusColor(invoiceToEmail.status)}>{invoiceToEmail.status}</Badge>
                  </div>
                </div>
              </div>
              
              {/* Parent Email Input */}
              <div>
                <Label htmlFor="parent-email" className="text-sm font-medium">
                  Parent/Guardian Email Address *
                </Label>
                <Input
                  id="parent-email"
                  type="email"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  placeholder="parent@example.com"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  The invoice will be sent to this email address
                </p>
              </div>
              
              {/* Payment Terms Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Payment Terms</p>
                    <p className="text-xs text-amber-700">Payment is required within 24 hours of receiving this invoice.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowSendEmailDialog(false);
              setInvoiceToEmail(null);
              setParentEmail('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendInvoiceEmail}
              disabled={sendingEmail || !parentEmail.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
